import { initContract } from '@ts-rest/core';
import { createApi, createQueryHooks } from './ts-rest';

/**
 * Pipeline Source Types
 */

interface PipelineSource {
  id: number;
  name: string;
  option_id: string;
  enabled: boolean;
  has_cio_sources: boolean;
}

interface PipelineSourceOption {
  option_id: string;
  name: string;
  icon: string;
  category: string;
  type: 'api' | 'connector' | 'database';
  hidden: boolean;
  code?: string;
  language?: string;
  library_install_command?: string;
  library_install_instructions?: string;
  library_install_learn_more_url?: string;
}

interface PipelineApiKey {
  id: number;
  api_key: string;
  created_by: number;
  source_id: number;
  created_at: number;
}

/**
 * Path Params
 */

interface WorkspacePathParams {
  workspaceId: number;
}

interface SourcePathParams extends WorkspacePathParams {
  sourceId: number;
}

interface ApiKeyPathParams extends SourcePathParams {
  apiKeyId: number;
}

/**
 * Contract
 */

const c = initContract();

const contract = c.router({
  listSources: {
    method: 'GET',
    path: '/workspaces/:workspaceId/sources',
    pathParams: c.type<WorkspacePathParams>(),
    responses: {
      200: c.type<{ sources: PipelineSource[] }>(),
    },
    summary: 'List all pipeline sources for a workspace',
  },
  createSource: {
    method: 'POST',
    path: '/workspaces/:workspaceId/sources',
    pathParams: c.type<WorkspacePathParams>(),
    body: c.type<{ source: { name: string; option_id: string } }>(),
    responses: {
      200: c.type<PipelineSource>(),
    },
    summary: 'Create a new pipeline source',
  },
  listSourceOptions: {
    method: 'GET',
    path: '/workspaces/:workspaceId/available_sources',
    pathParams: c.type<WorkspacePathParams>(),
    responses: {
      200: c.type<{ sources: PipelineSourceOption[] }>(),
    },
    summary: 'List available source type definitions',
  },
  listSourceApiKeys: {
    method: 'GET',
    path: '/workspaces/:workspaceId/sources/:sourceId/api_keys',
    pathParams: c.type<SourcePathParams>(),
    responses: {
      200: c.type<{ api_keys: PipelineApiKey[] }>(),
    },
    summary: 'List API keys for a pipeline source',
  },
  createSourceApiKey: {
    method: 'POST',
    path: '/workspaces/:workspaceId/sources/:sourceId/api_keys',
    pathParams: c.type<SourcePathParams>(),
    body: c.type<void>(),
    responses: {
      200: c.type<PipelineApiKey>(),
    },
    summary: 'Create an API key for a pipeline source',
  },
  getSourceStatuses: {
    method: 'GET',
    path: '/workspaces/:workspaceId/sources/statuses',
    pathParams: c.type<WorkspacePathParams>(),
    responses: {
      200: c.type<{ statuses: Record<string, number> }>(),
    },
    summary: 'Get last seen timestamps for all sources in a workspace',
  },
  deleteSourceApiKey: {
    method: 'DELETE',
    path: '/workspaces/:workspaceId/sources/:sourceId/api_keys/:apiKeyId',
    pathParams: c.type<ApiKeyPathParams>(),
    body: c.type<void>(),
    responses: {
      200: c.type<unknown>(),
    },
    summary: 'Delete an API key from a pipeline source',
  },
});

const { api } = createApi(contract, {
  baseUrl: '/cdp/api',
});

const { useQuery, useMutation } = createQueryHooks(contract, api);

/** @public */
export { api as cdpApi, useQuery as useCdpQuery, useMutation as useCdpMutation };
